import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  standalone: true,
  imports: [RouterLink],
  styleUrls: ['./cookie-consent.component.css'],
})
export class CookieConsentComponent implements OnInit {
  showBanner = false;
  private readonly CONSENT_COOKIE = 'gtm_cookie_consent';
  private readonly CONSENT_EXPIRY_DAYS = 365;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const consent = getCookie(this.CONSENT_COOKIE);
      if (consent == null) {
        this.showBanner = true;
      } else if (consent == 'necessaryOnly') {
        this.loadClarity();
        this.loadGoogleAnalytics();
        this.loadFacebookPixel();
      } else {
        this.loadGTM();
      }
    }
  }

  acceptCookies(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    setCookie(this.CONSENT_COOKIE, 'true', this.CONSENT_EXPIRY_DAYS);
    this.showBanner = false;
    this.loadGTM();
  }

  necessaryOnly(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    setCookie(this.CONSENT_COOKIE, 'necessaryOnly', this.CONSENT_EXPIRY_DAYS);
    this.showBanner = false;
    this.loadClarity();
    this.loadGoogleAnalytics();
    this.loadFacebookPixel();
  }

  dismissBanner(): void {
    this.showBanner = false;

    if (!isPlatformBrowser(this.platformId)) return;

    // Clear localStorage
    localStorage.clear();

    // Delete all cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

      // Expire cookie
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
    }
  }

  private loadGTM(): void {
    if (document.getElementById('gtm-script')) return;

    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-NLW7BR4R';
    document.head.appendChild(script);

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    this.addNoScriptFallback();
  }
  private addNoScriptFallback(): void {
    const iframe = this.renderer.createElement('iframe');
    this.renderer.setAttribute(
      iframe,
      'src',
      'https://www.googletagmanager.com/ns.html?id=GTM-NLW7BR4R'
    );
    this.renderer.setAttribute(iframe, 'height', '0');
    this.renderer.setAttribute(iframe, 'width', '0');
    this.renderer.setStyle(iframe, 'display', 'none');
    this.renderer.setStyle(iframe, 'visibility', 'hidden');
    this.renderer.appendChild(document.body, iframe);
  }
  private loadClarity(): void {
    const CLARITY_ID = 'nxhp2dus88';

    // Avoid duplicate script insertion
    if (document.getElementById('clarity-script')) return;

    const script = document.createElement('script');
    script.id = 'clarity-script';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
    document.head.appendChild(script);

    // Optionally initialize clarity queue
    if (!(window as any).clarity) {
      (window as any).clarity = function (...args: any[]) {
        ((window as any).clarity.q = (window as any).clarity.q || []).push(
          args
        );
      };
    }
  }

  private loadGoogleAnalytics(): void {
    const GA_MEASUREMENT_ID = 'G-0GZZ85TVHG';

    // Prevent duplicate script loading
    if (document.getElementById('ga-script')) return;

    // Create and insert gtag.js script
    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Once loaded, initialize gtag
    script.onload = () => {
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }

      (window as any).gtag = gtag;

      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID);
    };
  }

  private loadFacebookPixel(): void {
    const PIXEL_ID = '892972622880292';

    // Prevent duplicate script
    if (document.getElementById('fb-pixel-script')) return;

    // Dynamically inject the pixel script
    const script = document.createElement('script');
    script.id = 'fb-pixel-script';
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    // Declare fbq if not already defined
    if (!(window as any).fbq) {
      (function (f: any, b: Document, e: string, v: string, n?: any) {
        if (f.fbq) return;
        n = f.fbq = function (...args: any[]) {
          n!.callMethod ? n!.callMethod(...args) : n!.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n!.push = n;
        n!.loaded = true;
        n!.version = '2.0';
        n!.queue = [];
      })(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js'
      );
    }

    (window as any).fbq('init', PIXEL_ID);
    (window as any).fbq('set', 'agent', 'tmgoogletagmanager', PIXEL_ID);
    (window as any).fbq('track', 'PageView');

    this.addFacebookNoScriptFallback(PIXEL_ID);
  }

  private addFacebookNoScriptFallback(pixelId: string): void {
    if (document.getElementById('fb-noscript')) return;

    const img = document.createElement('img');
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';

    const noscript = document.createElement('noscript');
    noscript.id = 'fb-noscript';
    noscript.appendChild(img);

    document.body.appendChild(noscript);
  }
}
export function setCookie(name: string, value: string, days: number) {
  if (typeof document === 'undefined') return;

  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/; SameSite=Lax; Secure`;
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match[2]) : null;
}
